import { useState, useCallback, useEffect } from 'react';
import { AccountService, EmailChangeResult } from '../services/AccountService';
import { validateEmail } from '../utils/accountValidation';

export interface UseEmailChangeReturn {
  changeEmail: (newEmail: string) => Promise<EmailChangeResult>;
  verifyEmailChange: (token: string) => Promise<EmailChangeResult>;
  isSubmitting: boolean;
  error: string | null;
  verificationSent: boolean;
  verificationPending: boolean;
  success: string | null;
  clearError: () => void;
  clearSuccess: () => void;
  resetVerificationState: () => void;
}

/**
 * Custom hook for managing email change operations with verification state
 * Requirements: 1.2 - Email change with verification flow
 */
export function useEmailChange(): UseEmailChangeReturn {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationPending, setVerificationPending] = useState(false);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => {
        setSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const changeEmail = useCallback(async (newEmail: string): Promise<EmailChangeResult> => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);
    setVerificationSent(false);

    try {
      // Validate email format
      const validation = validateEmail(newEmail);
      if (!validation.isValid) {
        throw new Error(validation.error || 'Invalid email format');
      }

      const result = await AccountService.changeEmail(newEmail);
      
      if (!result.success) {
        setError(result.message);
      } else {
        setVerificationSent(result.verificationSent || false);
        setVerificationPending(result.verificationSent || false);
        setSuccess(result.message);
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to change email';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const verifyEmailChange = useCallback(async (token: string): Promise<EmailChangeResult> => {
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      if (!token || token.trim().length === 0) {
        throw new Error('Verification token is required');
      }

      const result = await AccountService.verifyEmailChange(token);
      
      if (!result.success) {
        setError(result.message);
      } else {
        setVerificationSent(false);
        setVerificationPending(false);
        setSuccess(result.message || 'Email successfully updated');
      }
      
      return result;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to verify email change';
      setError(errorMessage);
      return {
        success: false,
        message: errorMessage
      };
    } finally {
      setIsSubmitting(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  const clearSuccess = useCallback(() => {
    setSuccess(null);
  }, []);

  const resetVerificationState = useCallback(() => {
    setVerificationSent(false);
    setVerificationPending(false);
    setError(null);
    setSuccess(null);
  }, []);

  return {
    changeEmail,
    verifyEmailChange,
    isSubmitting,
    error,
    verificationSent,
    verificationPending,
    success,
    clearError,
    clearSuccess,
    resetVerificationState
  };
}